import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { trackEvent } from "@/lib/analytics";

const anchors = [
  { label: "Approche", href: "#approche" },
  { label: "Réalisations", href: "#realisations" },
  { label: "À propos", href: "#a-propos" },
];

const services = [
  { label: "Reels", to: "/reels" },
  { label: "Podcasts", to: "/podcasts" },
  { label: "Événements", to: "/evenements" },
  { label: "Formations", to: "/formations" },
  { label: "Publicités", to: "/publicites" },
  { label: "Tous nos services", to: "/services" },
];

const resources = [
  { label: "Études de cas", to: "/etudes-de-cas" },
  { label: "Articles", to: "/articles" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Tarifs", to: "/tarifs" },
];

const scrollTo = (id: string) => {
  const el = document.querySelector(id);
  el?.scrollIntoView({ behavior: "smooth", block: "start" });
};

const dropdownLinkCls =
  "block rounded-lg px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground";

const mobileLinkCls =
  "flex min-h-[48px] items-center rounded-lg px-3 text-base text-foreground transition-colors hover:bg-secondary";

/** Menu déroulant : les liens restent toujours dans le DOM (masqués visuellement) donc crawlables. */
const Dropdown = ({
  label,
  items,
  open,
  onToggle,
  onEnter,
  onLeave,
}: {
  label: string;
  items: { label: string; to: string }[];
  open: boolean;
  onToggle: () => void;
  onEnter: () => void;
  onLeave: () => void;
}) => (
  <li className="relative" onMouseEnter={onEnter} onMouseLeave={onLeave}>
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      className="flex items-center gap-1 text-sm text-muted-foreground transition-colors hover:text-foreground"
    >
      {label}
      <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
    </button>
    <div
      className={`absolute left-1/2 top-full z-50 w-56 -translate-x-1/2 pt-3 transition-opacity duration-150 ${
        open ? "opacity-100" : "pointer-events-none opacity-0"
      }`}
    >
      <ul className="rounded-2xl border border-border bg-background p-2 shadow-xl">
        {items.map((item) => (
          <li key={item.to}>
            <Link to={item.to} className={dropdownLinkCls}>
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  </li>
);

const NavV2 = () => {
  const [open, setOpen] = useState(false);
  const [dropdown, setDropdown] = useState<string | null>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToForm = () => {
    trackEvent("primary_cta_clicked", { location: "navigation" });
    setOpen(false);
    scrollTo("#formulaire");
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-background/90 backdrop-blur-md border-b border-border/60" : "bg-transparent"
      }`}
    >
      <nav
        aria-label="Navigation principale"
        className="mx-auto flex h-[68px] max-w-[1280px] items-center justify-between px-5 md:h-20 md:px-8"
      >
        <Logo variant="full" size="sm" />

        <ul className="hidden items-center gap-7 lg:flex">
          <Dropdown
            label="Services"
            items={services}
            open={dropdown === "services"}
            onToggle={() => setDropdown("services")}
            onEnter={() => setDropdown("services")}
            onLeave={() => setDropdown(null)}
          />
          <Dropdown
            label="Ressources"
            items={resources}
            open={dropdown === "ressources"}
            onToggle={() => setDropdown("ressources")}
            onEnter={() => setDropdown("ressources")}
            onLeave={() => setDropdown(null)}
          />
          {anchors.map((link) => (
            <li key={link.href}>
              <button
                type="button"
                onClick={() => scrollTo(link.href)}
                className="text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                {link.label}
              </button>
            </li>
          ))}
          <li>
            <Link to="/accompagnement" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
              Accompagnement
            </Link>
          </li>
          <li>
            <Link to="/plan-de-match" className="text-sm text-muted-foreground transition-colors hover:text-foreground">
              Plan de match
            </Link>
          </li>
        </ul>

        <div className="flex items-center gap-2">
          <Button
            onClick={goToForm}
            className="hidden rounded-full bg-primary px-5 text-primary-foreground hover:bg-primary/90 sm:inline-flex"
          >
            Nous parler de votre entreprise
          </Button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? "Fermer le menu" : "Ouvrir le menu"}
            aria-expanded={open}
            className="flex h-11 w-11 items-center justify-center rounded-lg border border-border text-foreground lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      {/* Menu mobile : toujours présent dans le DOM (masqué visuellement) pour rester crawlable */}
      <div
        className={`overflow-hidden bg-background transition-[max-height,opacity] duration-300 lg:hidden ${
          open ? "max-h-[80vh] overflow-y-auto border-t border-border opacity-100" : "pointer-events-none max-h-0 opacity-0"
        }`}
      >
        <div className="mx-auto max-w-[1280px] px-5 pb-6 pt-3">
          <p className="px-3 pb-1 text-xs uppercase tracking-[0.18em] text-muted-foreground/70">Services</p>
          <ul>
            {services.map((item) => (
              <li key={item.to}>
                <Link to={item.to} onClick={() => setOpen(false)} className={mobileLinkCls}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <p className="mt-3 px-3 pb-1 text-xs uppercase tracking-[0.18em] text-muted-foreground/70">Ressources</p>
          <ul>
            {resources.map((item) => (
              <li key={item.to}>
                <Link to={item.to} onClick={() => setOpen(false)} className={mobileLinkCls}>
                  {item.label}
                </Link>
              </li>
            ))}
          </ul>

          <p className="mt-3 px-3 pb-1 text-xs uppercase tracking-[0.18em] text-muted-foreground/70">Agence</p>
          <ul>
            <li>
              <Link to="/accompagnement" onClick={() => setOpen(false)} className={mobileLinkCls}>
                Accompagnement
              </Link>
            </li>
            <li>
              <Link to="/plan-de-match" onClick={() => setOpen(false)} className={mobileLinkCls}>
                Plan de match
              </Link>
            </li>
            {anchors.map((link) => (
              <li key={link.href}>
                <button
                  type="button"
                  onClick={() => {
                    setOpen(false);
                    scrollTo(link.href);
                  }}
                  className="flex min-h-[48px] w-full items-center rounded-lg px-3 text-left text-base text-foreground transition-colors hover:bg-secondary"
                >
                  {link.label}
                </button>
              </li>
            ))}
          </ul>

          <div className="pt-3">
            <Button
              onClick={goToForm}
              className="w-full rounded-full bg-primary py-5 text-base text-primary-foreground hover:bg-primary/90"
            >
              Nous parler de votre entreprise
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default NavV2;
